import { _getLogger } from '@doxyme/analytics';
import { Specs, attachHandler } from '@doxyme/extensions-common';
import ExtensionCapability from '../../capability';
import type Instance from '../../instance';
import { logErrorAndThrowBind, type LogErrorAndThrowBound } from '../../utils';

const logger = _getLogger('multiplayer', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

type ParticipantAction = Specs.Multiplayer.ParticipantAction;
type Action = Specs.Multiplayer.Action;
type Message = Specs.Multiplayer.Message;
type ParticipantMessage = Specs.Multiplayer.ParticipantMessage;
type Command = Specs.Multiplayer.Command;
type ParticipantId = Specs.Multiplayer.ParticipantId;
type Participant = Specs.Multiplayer.Participant;
type InternalParticipant = Specs.Multiplayer.InternalParticipant;

const Role = Specs.ReceiveContext.Role;
const CommandType = Specs.Multiplayer.CommandType;

const { Multiplayer } = Specs;

export type CommandHandler = (
  participantId: Specs.Multiplayer.ParticipantId,
  command: Specs.Multiplayer.Command,
) => Promise<void>;

function diffset<TK, TV>(a: Map<TK, TV>, b: Map<TK, TV>): Map<TK, TV> {
  return new Map([...a].filter(([key]) => !b.has(key)));
}

export default abstract class MultiplayerCapability<
  Constraints extends Specs.Multiplayer.Constraints,
> extends ExtensionCapability<Constraints> {
  protected participants: Map<ParticipantId, InternalParticipant> = new Map();

  protected readonly commandHandlers: CommandHandler[] = [];

  protected readonly role: Specs.ReceiveContext.Role;

  protected readonly realtimeId: ParticipantId;

  private readonly promiseQueue: Map<string, Promise<void>> = new Map();

  constructor(
    spec: Specs.CapabilitySpec,
    role: Specs.ReceiveContext.Role,
    realtimeId: ParticipantId,
  ) {
    super(spec);
    this.role = role;
    this.realtimeId = realtimeId;
  }

  /**
   * Update the list of participants and notify all extensions
   * @param participants - The list of participants
   * @example
   * ```ts
   * multiplayer.updateParticipants([{id: 'participant1'}, {id: 'participant2'}]);
   * ```
   */
  updateParticipants(participants: InternalParticipant[]): void {
    if (!this.isEveryParticipantValid(participants)) {
      logErrorAndThrow('[updateParticipants] Invalid multiplayer participant');
    }

    const updated = new Map<ParticipantId, InternalParticipant>(
      participants.map((p) => [p.id, p]),
    );

    const joins = diffset(updated, this.participants);
    const departs = diffset(this.participants, updated);

    this.participants = updated;

    for (const instance of Object.values(this.instances)) {
      if (joins.size > 0) {
        const refinedJoins = this.getRefinedParticipants(instance, joins);
        this.notifyJoins(instance, refinedJoins);
      }
      if (departs.size > 0) {
        const refinedDeparts = this.getRefinedParticipants(instance, departs);
        this.notifyDeparts(instance, refinedDeparts);
      }
      if (joins.size > 0 || departs.size > 0) {
        this.notifyParticipants(
          instance,
          this.getRefinedParticipants(instance),
        );
      }
    }
  }

  private isEveryParticipantValid(
    participants: InternalParticipant[],
  ): boolean {
    return participants.every((p) => Multiplayer.isValidParticipant(p));
  }

  private getRefinedParticipants(
    instance: Instance,
    participants = this.participants,
  ): Participant[] {
    return [...participants.values()].map((p) =>
      this.refineContext(instance, p),
    );
  }

  protected requestParticipants(
    payload: never,
    instance: Instance<Constraints>,
  ): void {
    this.notifyParticipants(instance, this.getRefinedParticipants(instance));
  }

  protected requestRealtimeId(
    payload: never,
    instance: Instance<Constraints>,
  ): void {
    this.notifyRealtimeId(instance, this.realtimeId);
  }

  protected abstract notifyJoins(
    instance: Instance<Constraints>,
    joins: Participant[],
  ): void;

  protected abstract notifyDeparts(
    instance: Instance<Constraints>,
    departs: Participant[],
  ): void;

  protected abstract notifyParticipants(
    instance: Instance<Constraints>,
    participants: Participant[],
  ): void;

  protected abstract notifyRealtimeId(
    instance: Instance<Constraints>,
    realtimeId: ParticipantId,
  ): void;

  /**
   * Registers a handler for commands (messages or actions). A command is a wrapper for an action which assigns a recipient and is designed to be sent via the realtime module.
   * @param handler - Callback function for handling commands
   * @description Extensions are designed to support sending commands to other user's extensions via multiplayer capabilities. Intended use would be to utilize the realtime module inside this callback to forward the command to the other extension.
   * @returns A function to unregister the handler
   * @example
   * ```ts
   * multiplayer.onCommand((participantId: ParticipantId, command: Command) => {
   *  console.log(`Received command ${command.type} for ${participantId}`);
   *  // Forward command to other extension
   *  const participant = realtime.membersMap[participantId];
   *  realtime.sendMessage(participant.identity, command);
   * });
   * ```
   */
  onCommand(handler: CommandHandler): () => void {
    return attachHandler<CommandHandler>(this.commandHandlers, handler);
  }

  /**
   * Send a command (message or action) to an extension
   * @param from - Participant ID command was received from
   * @param command - Command definition
   * @description Forwards a command to the extensions that were received by the realtime module event listener.
   * @example
   * ```ts
   *  realtime.on([RealtimeEventType.Message], (event: RealtimeEvent, type: RealtimeEventType) => {
   *    console.log('realtime message received', type, event);
   *    multiplayer.handleCommand(event.from.id, event.data as Command);
   *  });
   * ```
   */
  handleCommand(from: ParticipantId, command: Command): void {
    const instance = this.instances[command.instanceId];
    if (!instance) {
      logErrorAndThrow(
        `[handleCommand] Unknown instance: ${command.instanceId}`,
      );
    }

    if (command.type === CommandType.Message) {
      const payload = command.payload as Message;
      this.notifyMessage(instance, { participant: from, ...payload });
      return;
    }

    if (command.type === CommandType.Action) {
      if (this.role !== Role.Patient) {
        logErrorAndThrow(
          `[handleCommand] Command unsupported by Role: ${command.type}`,
        );
      }

      const { action, payload } = command.payload as Action;

      instance.receive([action, payload] as Constraints['actionPayloadTuple']);
      return;
    }

    logErrorAndThrow(`[handleCommand] Unsupported Command: ${command.type}`);
  }

  protected abstract notifyMessage(
    instance: Instance<Constraints>,
    message: ParticipantMessage,
  ): void;

  protected sendMessage(
    payload: ParticipantMessage,
    instance: Instance<Constraints>,
  ): void {
    void this.handleSendMessage(payload, instance);
  }

  protected async handleSendMessage(
    payload: ParticipantMessage,
    instance: Instance<Constraints>,
  ): Promise<void> {
    const participantId = payload.participant;

    if (!this.participants.has(participantId)) {
      logger.warn(`[sendMessage] Unknown participant: ${participantId}`);
      return undefined;
    }

    const command: Command = {
      type: CommandType.Message,
      instanceId: instance.id,
      payload: {
        type: payload.type,
        payload: payload.payload,
      },
    };

    await Promise.all(
      this.commandHandlers.map(async (handler) =>
        handler(participantId, command),
      ),
    );
  }

  protected sendMessageAll(
    payload: Message,
    instance: Instance<Constraints>,
  ): void {
    [...this.participants.keys()].forEach(
      (participantId) =>
        void this.handleSendMessage(
          { ...payload, participant: participantId },
          instance,
        ),
    );
  }

  protected sendAction(
    payload: ParticipantAction,
    instance: Instance<Constraints>,
  ): void {
    if (this.role !== Role.Provider) {
      logErrorAndThrow(
        `[sendAction] Command unsupported by Role: ${CommandType.Action}`,
      );
    }

    void this.handleSendAction(payload, instance);
  }

  protected async handleSendAction(
    payload: ParticipantAction,
    instance: Instance<Constraints>,
  ): Promise<void> {
    const participantId = payload.participant;

    if (!this.participants.has(participantId)) {
      logger.warn(`[sendAction] Unknown participant: ${participantId}`);
      return undefined;
    }

    const command: Command = {
      type: CommandType.Action,
      instanceId: instance.id,
      payload: {
        action: payload.action,
        payload: payload.payload,
      },
    };

    await Promise.all(
      this.commandHandlers.map(async (handler) =>
        handler(participantId, command),
      ),
    );
  }

  protected sendActionAll(
    payload: Action,
    instance: Instance<Constraints>,
  ): void {
    if (this.role !== Role.Provider) {
      logErrorAndThrow(
        `[sendActionAll] Command unsupported by Role: ${CommandType.Action}`,
      );
    }

    [...this.participants.keys()].forEach(
      (participantId) =>
        void this.handleSendAction(
          { ...payload, participant: participantId },
          instance,
        ),
    );
  }
}
